import React, { useState, createContext, useContext, useCallback } from 'react';
import { uuid } from 'uuidv4';
import ModalContainer from '../components/ModalContainer';
import styled, { keyframes } from 'styled-components';
import { FiX } from 'react-icons/fi';
 

export interface IModalMessage {
  id: string;
  title: string;
  content: JSX.Element;
  key?: string;
  theme?: string;
  closeButton?: boolean;
}

export interface IModalContextData {
  addModal(message: Omit<IModalMessage, 'id'>): void;
    addModalV2(message: Omit<IModalMessage, 'id'>): void;
  removeModal(id: string): void;
  prepareKey(): string;
}

const ModalContext = createContext<IModalContextData>({} as IModalContextData);


export interface IModalV2Message {
  /** Chave única para removeModal(key). Gerada automaticamente se omitida. */
  key?: string;
  title: string;
  content: JSX.Element;
  /** Botões no rodapé. O chamador é responsável por chamar removeModal(key) para fechar. */
  actions?: JSX.Element;
  /** Largura máxima da caixa em px. Padrão: 480 */
  maxWidth?: number;
}


// ─── Styled components do V2 ─────────────────────────────────────────────────

const v2Appear = keyframes`
  from { opacity: 0; transform: translateY(-10px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0)     scale(1);    }
`;

const V2Wrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  padding: 32px 16px;
`;

const V2Box = styled.div<{ $maxWidth: number }>`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px 24px 20px;
  width: 100%;
  max-width: ${p => p.$maxWidth || '1200'}px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.14);
  animation: ${v2Appear} 0.18s ease;
`;

const V2Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const V2Title = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.4;
  flex: 1;
`;

const V2CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #9ca3af;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
  &:hover {
    border-color: #6f2176;
    color: #6f2176;
    background: rgba(111, 33, 118, 0.05);
  }
`;

const V2Footer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
`;

// ─── V2ModalWrapper — renderizado dentro do ModalContainer (dentro do Provider)

const V2ModalWrapper: React.FC<{
  id: string;
  title: string;
  body: JSX.Element;
  actions?: JSX.Element;
  maxWidth: number;
}> = ({ id, title, body, actions, maxWidth }) => {
  const { removeModal } = useModal();

  return (
    <V2Wrap>
      <V2Box $maxWidth={maxWidth}>
        <V2Head>
          <V2Title>{title}</V2Title>
          <V2CloseBtn title="Fechar" onClick={() => removeModal(id)}>
            <FiX size={14} />
          </V2CloseBtn>
        </V2Head>

        {body}

        {actions && <V2Footer>{actions}</V2Footer>}
      </V2Box>
    </V2Wrap>
  );
};


const ModalProvider: React.FC = ({ children }) => {
  const [messages, setMessages] = useState<IModalMessage[]>([]);
  const [className, setClassName] = useState('');

  const [closeButton, setCloseButton] = useState(true);

  const prepareKey = useCallback(() => uuid(), []);

  const addModal = useCallback(
    ({ title, content, key, theme = '', closeButton = true }: Omit<IModalMessage, 'id'>) => {
      const id = key || uuid();
      const newMessage = {
        id,
        title,
        content,
        theme,
      };
      setCloseButton(state => closeButton);
      setClassName(state => theme);
      setMessages(state => [...state, newMessage]);

      return id;
    },
    [],
  );
  const removeModal = useCallback((id: string) => {
    setMessages(state => state.filter(message => message.id !== id));
  }, []);


  // ── addModalV2 ─────────────────────────────────────────────────────────────
  const addModalV2 = useCallback(
    ({ title, content, key, actions, maxWidth = 1200 }: IModalV2Message): string => {
      const id = key ?? `v2-${Date.now()}`;
      addModal({
        key: id,
        title: '',
        closeButton: false,
        theme: 'whiteModalNone',
       
        content: (
          <V2ModalWrapper
            id={id}
            title={title}
            body={content}
            actions={actions}
            maxWidth={maxWidth}
          />
        ),
      });
      return id;
    },
    [addModal],
  );


  return (
    <ModalContext.Provider value={{ addModal, addModalV2, removeModal, prepareKey }}>
      {children}
      <ModalContainer messages={messages} className={className} closeButton={closeButton} />
    </ModalContext.Provider>
  );
};

function useModal(): IModalContextData {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
}

export { useModal, ModalProvider };
